/**
 * React Query hooks for orders data.
 */

import { useQuery } from '@tanstack/react-query';
import type { SalesOrderStatus } from 'scheduler-types';
import { api } from '../lib/api';

/** Order summary from GET /api/v1/orders */
export interface OrderSummary {
  id: string;
  gpSalesOrderNumber: string;
  customerName: string;
  priority: string;
  earliestPromisedShipDate: string;
  latestPromisedShipDate: string;
  expectedShipDate: string | null;
  expectedShipDateWorst: string | null;
  status: SalesOrderStatus;
  lineItemsAtRisk: number;
  lineItemCount: number;
}

interface OrdersResponse {
  data: OrderSummary[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

interface UseOrdersOptions {
  status?: SalesOrderStatus;
  limit?: number;
  offset?: number;
}

/**
 * Fetch orders list with optional filtering.
 */
export function useOrders(options: UseOrdersOptions = {}) {
  const { status, limit = 50, offset = 0 } = options;

  return useQuery({
    queryKey: ['orders', { status, limit, offset }],
    queryFn: async () => {
      const response = await api.get<OrdersResponse>('/orders', {
        params: { status, limit, offset },
      });
      return response;
    },
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // Refetch every minute for real-time updates
    refetchOnWindowFocus: false, // Prevent duplicate calls on tab switch
  });
}

/**
 * Determine the risk status of an order based on dates.
 */
export function getOrderRiskStatus(order: OrderSummary): 'on-track' | 'at-risk' | 'late' {
  if (order.lineItemsAtRisk > 0) {
    return 'at-risk';
  }

  if (!order.expectedShipDate || !order.latestPromisedShipDate) {
    return 'on-track';
  }

  const expected = new Date(order.expectedShipDate);
  const promised = new Date(order.latestPromisedShipDate);

  if (expected > promised) {
    return 'late';
  }

  return 'on-track';
}
